// services/image.service.js
import imagesClient from '../api/imagesClient';
import { handleApiError } from '../api/assistantClient';

/**
 * Servicio para gestionar Imágenes generadas
 * Endpoint base: /accounts/{accountId}/images
 */
class ImageService {
  /**
   * Listar imágenes generadas por un assistant (agent)
   *
   * @param {Object} params
   * @param {string} params.accountId   - Cuenta (path)
   * @param {string} params.assistantId - Filtro por agent (origin_assistant_id)
   * @param {string} [params.status]    - pending | processing | completed | failed
   * @param {string} [params.threadId]  - Filtro opcional por conversación puntual
   * @param {string} [params.sort='created_at:desc'] - created_at:desc | created_at:asc
   * @param {number} [params.limit=50]  - rango 1–200
   * @param {number} [params.offset=0]  - paginación
   * @returns {Promise<{success: boolean, data?: Array, error?: Object}>}
   */
  async listImagesByAssistant({ accountId, assistantId, status, threadId, sort = 'created_at:desc', limit = 50, offset = 0 }) {
    if (!accountId || !assistantId) {
      return { success: false, error: { message: 'accountId y assistantId requeridos' } };
    }

    try {
      const params = { assistantId, sort, limit, offset };
      if (status) params.status = status;
      if (threadId) params.threadId = threadId;

      const response = await imagesClient.get(`/accounts/${accountId}/images`, { params });
      return { success: true, data: response.data?.images || [] };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Enviar una solicitud de generación de imagen (async).
   *
   * El Image Service encola el trabajo y responde de inmediato con
   * { requestId, imageIds, status: 'pending', coinsPreChecked }. El worker
   * procesa en segundo plano; usar getImage() para pollear hasta
   * status === 'completed' y obtener la URL presignada (24h).
   *
   * Modelo por defecto: 'google/gemini-2.5-flash-image' (el más barato del
   * catálogo, ~15 coins). origin.type='direct_api' porque la generación no
   * proviene de un assistant ni de un workflow (p. ej. avatar de perfil).
   *
   * @param {Object} params
   * @param {string} params.accountId
   * @param {string} [params.projectId] - Opcional (zenitailab !859). Una imagen
   *   puede no pertenecer a ningún proyecto — un avatar público no tiene, y la
   *   foto de perfil tampoco. El cobro y la cuota van por cuenta.
   * @param {string} params.prompt
   * @param {string} [params.providerModelId='google/gemini-2.5-flash-image']
   * @param {number} [params.n=1]
   * @returns {Promise<{success: boolean, data?: Object, error?: Object}>}
   */
  async generateImage({ accountId, projectId, prompt, providerModelId = 'google/gemini-2.5-flash-image', n = 1 }) {
    if (!accountId || !prompt) {
      return { success: false, error: { message: 'accountId y prompt son requeridos' } };
    }

    try {
      const response = await imagesClient.post(`/accounts/${accountId}/images`, {
        // Se omite, nunca se manda null: el schema rechaza un projectId que no
        // parsee como UUID, así que enviarlo vacío sería un 422 en vez de una
        // imagen sin proyecto.
        ...(projectId ? { projectId } : {}),
        providerModelId,
        prompt,
        n,
        operation: 'generate',
        origin: { type: 'direct_api' },
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Obtener una imagen por ID (para pollear pending/processing hasta completed)
   */
  async getImage({ accountId, imageId }) {
    if (!accountId || !imageId) {
      return { success: false, error: { message: 'accountId e imageId requeridos' } };
    }

    try {
      const response = await imagesClient.get(`/accounts/${accountId}/images/${imageId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }
}

export default new ImageService();
